import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Code,
  Tag,
  ChevronDown,
  Eye,
  FileCode,
  Upload,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Minus,
  Palette,
  Highlighter,
  RemoveFormatting,
} from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const MERGE_TAGS = [
  { label: "First name", token: "{{first_name}}" },
  { label: "Last name", token: "{{last_name}}" },
  { label: "Full name", token: "{{name}}" },
  { label: "Email", token: "{{email}}" },
  { label: "Unsubscribe URL", token: "{{unsubscribe_url}}" },
  { label: "Subject Line", token: "{{subject_line}}" },
  { label: "Message Body", token: "{{message_body}}" },
];

const FONT_FAMILIES = [
  { label: "Default font", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
];

const FONT_SIZES = [
  { label: "Size", value: "" },
  { label: "10 px", value: "10px" },
  { label: "12 px", value: "12px" },
  { label: "14 px", value: "14px" },
  { label: "16 px", value: "16px" },
  { label: "18 px", value: "18px" },
  { label: "20 px", value: "20px" },
  { label: "24 px", value: "24px" },
  { label: "28 px", value: "28px" },
  { label: "32 px", value: "32px" },
  { label: "36 px", value: "36px" },
  { label: "48 px", value: "48px" },
];

const TEXT_COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
  "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
  "#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
  "#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
];

const HIGHLIGHT_COLORS = [
  "#ffff00", "#ffd966", "#fce5cd", "#d9ead3", "#cfe2f3", "#d9d2e9", "#ffffff", "transparent",
];

/** Strip document wrappers so TipTap renders body content as WYSIWYG, not raw tags. */
export function normalizeEmailHtml(html) {
  if (!html || !String(html).trim()) return "";
  let out = String(html).trim();
  if (/<body[\s>]/i.test(out)) {
    const match = out.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (match) out = match[1].trim();
  }
  return out
    .replace(/<!DOCTYPE[^>]*>/gi, "")
    .replace(/<\/?html[^>]*>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .trim();
}

const ToolButton = ({ active, onClick, disabled, children, label, "data-testid": testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    data-testid={testId}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-sm border transition-colors ${
      active ? "bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900 dark:border-white" : "bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"
    } disabled:opacity-40`}
  >
    {children}
  </button>
);

const ModeButton = ({ active, onClick, children, label, "data-testid": testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    data-testid={testId}
    className={`h-8 px-2.5 inline-flex items-center gap-1.5 rounded-sm border text-xs font-semibold transition-colors ${
      active ? "bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900 dark:border-white" : "bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"
    }`}
  >
    {children}
  </button>
);

const ToolbarSelect = ({ value, onChange, options, className = "", "data-testid": testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={`h-8 max-w-[9.5rem] rounded-sm border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-neutral-700 dark:text-neutral-300 text-xs font-medium px-2 pr-7 truncate ${className}`}
  >
    {options.map((o) => (
      <option key={o.value || o.label} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const ColorGrid = ({ colors, current, onPick, allowClear }) => (
  <div className="grid grid-cols-5 gap-1.5">
    {allowClear && (
      <button
        type="button"
        title="Remove color"
        onClick={() => onPick("")}
        className="h-7 w-7 rounded-sm border border-neutral-300 dark:border-neutral-600 text-[10px] font-bold"
      >
        ×
      </button>
    )}
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        title={c}
        onClick={() => onPick(c)}
        className={`h-7 w-7 rounded-sm border ${current === c ? "ring-2 ring-neutral-900 dark:ring-white" : "border-neutral-200 dark:border-neutral-700"}`}
        style={{ backgroundColor: c === "transparent" ? "transparent" : c }}
      />
    ))}
  </div>
);

export default function RichTextEditor({ value, onChange, placeholder = "Write several sentences of text — image-only emails often fail spam checks" }) {
  const [mode, setMode] = useState("visual");
  const [sourceHtml, setSourceHtml] = useState(() => normalizeEmailHtml(value || ""));
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [toolbarTick, setToolbarTick] = useState(0);
  const fileInputRef = useRef(null);

  const emitChange = useCallback((html) => {
    onChange?.(html);
  }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
      }),
      TextStyleKit,
      Highlight.configure({ multicolor: true }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      LinkExtension.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: normalizeEmailHtml(value || ""),
    onUpdate({ editor: ed }) {
      if (mode === "visual") {
        const html = ed.getHTML();
        setSourceHtml(html);
        emitChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: "email-preview prose prose-sm max-w-none focus:outline-none min-h-[360px] px-4 py-3",
        "data-testid": "rich-editor-area",
      },
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;
    const refresh = () => setToolbarTick((n) => n + 1);
    editor.on("selectionUpdate", refresh);
    editor.on("transaction", refresh);
    return () => {
      editor.off("selectionUpdate", refresh);
      editor.off("transaction", refresh);
    };
  }, [editor]);

  // Sync external value (template load, AI apply, etc.)
  useEffect(() => {
    const normalized = normalizeEmailHtml(value || "");
    setSourceHtml(normalized);
    if (!editor || mode !== "visual") return;
    const current = editor.getHTML();
    if (normalized !== current) {
      editor.commands.setContent(normalized || "<p></p>", { emitUpdate: false });
    }
  }, [value, editor, mode]);

  const switchToVisual = () => {
    if (editor) {
      const html = sourceHtml || "<p></p>";
      editor.commands.setContent(html, { emitUpdate: false });
      emitChange(editor.getHTML());
    } else {
      emitChange(sourceHtml);
    }
    setMode("visual");
  };

  const switchToSource = () => {
    if (editor) {
      setSourceHtml(editor.getHTML());
    }
    setMode("source");
  };

  const handleSourceChange = (e) => {
    const html = e.target.value;
    setSourceHtml(html);
    emitChange(html);
  };

  if (!editor) {
    return (
      <div className="rounded-sm border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950" data-testid="rich-text-editor">
        <div className="h-10 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900" />
        <div className="h-72 bg-neutral-50 dark:bg-neutral-900 animate-pulse" />
      </div>
    );
  }

  void toolbarTick;

  const textStyle = editor.getAttributes("textStyle") || {};
  const currentFont = textStyle.fontFamily || "";
  const currentSize = textStyle.fontSize || "";
  const currentColor = textStyle.color || "";
  const currentHighlight = editor.getAttributes("highlight")?.color || "";

  const insertImage = (url) => {
    if (!url?.trim() || !editor) return;
    editor.chain().focus().setImage({ src: url.trim() }).run();
    setImageOpen(false);
    setImageUrl("");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPEG, PNG, GIF, or WebP)");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      insertImage(data.url);
      toast.success("Image added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const applyFontFamily = (family) => {
    if (!family) {
      editor.chain().focus().unsetFontFamily().run();
      return;
    }
    editor.chain().focus().setFontFamily(family).run();
  };

  const applyFontSize = (size) => {
    if (!size) {
      editor.chain().focus().unsetFontSize().run();
      return;
    }
    editor.chain().focus().setFontSize(size).run();
  };

  const applyTextColor = (color) => {
    if (!color) {
      editor.chain().focus().unsetColor().run();
      return;
    }
    editor.chain().focus().setColor(color).run();
  };

  const applyHighlight = (color) => {
    if (!color || color === "transparent") {
      editor.chain().focus().unsetHighlight().run();
      return;
    }
    editor.chain().focus().setHighlight({ color }).run();
  };

  const clearFormatting = () => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  return (
    <div className="email-editor-wysiwyg rounded-sm border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
        <ModeButton label="Visual editor" data-testid="rte-mode-visual" active={mode === "visual"} onClick={switchToVisual}>
          <Eye className="h-3.5 w-3.5" /> Visual
        </ModeButton>
        <ModeButton label="HTML source code" data-testid="rte-mode-source" active={mode === "source"} onClick={switchToSource}>
          <FileCode className="h-3.5 w-3.5" /> Source
        </ModeButton>
        <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

        {mode === "visual" && (
          <>
            <ToolbarSelect
              data-testid="rte-font-family"
              value={currentFont}
              options={FONT_FAMILIES}
              onChange={applyFontFamily}
              className="max-w-[7.5rem]"
            />
            <ToolbarSelect
              data-testid="rte-font-size"
              value={currentSize}
              options={FONT_SIZES}
              onChange={applyFontSize}
              className="max-w-[5.5rem]"
            />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Text color"
                  data-testid="rte-text-color"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-sm border bg-white dark:bg-neutral-950 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"
                >
                  <Palette className="h-3.5 w-3.5" style={{ color: currentColor || undefined }} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-52 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Text color</p>
                <ColorGrid colors={TEXT_COLORS} current={currentColor} onPick={applyTextColor} allowClear />
                <Input
                  type="color"
                  value={currentColor || "#000000"}
                  onChange={(e) => applyTextColor(e.target.value)}
                  className="h-8 w-full p-0 border-0 cursor-pointer"
                />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Highlight"
                  data-testid="rte-highlight"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-sm border bg-white dark:bg-neutral-950 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"
                >
                  <Highlighter className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-52 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Highlight</p>
                <ColorGrid colors={HIGHLIGHT_COLORS} current={currentHighlight} onPick={applyHighlight} allowClear />
              </PopoverContent>
            </Popover>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

            <ToolButton label="Bold" data-testid="rte-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
              <Bold className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Italic" data-testid="rte-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
              <Italic className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Underline" data-testid="rte-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
              <UnderlineIcon className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Strikethrough" data-testid="rte-strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
              <Strikethrough className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Clear formatting" data-testid="rte-clear-format" onClick={clearFormatting}>
              <RemoveFormatting className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

            <ToolButton label="Heading 1" data-testid="rte-h1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
              <Heading1 className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Heading 2" data-testid="rte-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
              <Heading2 className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Heading 3" data-testid="rte-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
              <Heading3 className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

            <ToolButton label="Align left" data-testid="rte-align-left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
              <AlignLeft className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Align center" data-testid="rte-align-center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
              <AlignCenter className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Align right" data-testid="rte-align-right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
              <AlignRight className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Justify" data-testid="rte-align-justify" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
              <AlignJustify className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

            <ToolButton label="Bullet list" data-testid="rte-bullet" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <List className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Numbered list" data-testid="rte-ordered" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <ListOrdered className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Quote" data-testid="rte-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
              <Quote className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Horizontal rule" data-testid="rte-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
              <Minus className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />

            <ToolButton label="Link" data-testid="rte-link" active={editor.isActive("link")} onClick={setLink}>
              <LinkIcon className="h-3.5 w-3.5" />
            </ToolButton>
            <Popover open={imageOpen} onOpenChange={setImageOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Insert image"
                  data-testid="rte-image"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-sm border bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400 transition-colors"
                >
                  <ImageIcon className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Insert image</p>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleImageUpload} />
                <Button type="button" variant="outline" className="w-full rounded-sm justify-center" disabled={uploadingImage} onClick={() => fileInputRef.current?.click()} data-testid="rte-image-upload">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingImage ? "Uploading..." : "Upload from computer"}
                </Button>
                <div className="text-xs text-neutral-400 text-center">or paste a URL</div>
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" className="rounded-sm text-sm" data-testid="rte-image-url" />
                <Button type="button" className="w-full rounded-sm btn-brand font-semibold" disabled={!imageUrl.trim()} onClick={() => insertImage(imageUrl)} data-testid="rte-image-url-insert">
                  Insert URL
                </Button>
              </PopoverContent>
            </Popover>
            <ToolButton label="Inline code" data-testid="rte-code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}>
              <Code className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="Insert merge tag"
                  data-testid="rte-merge-tags"
                  className="h-8 px-2 inline-flex items-center gap-1 rounded-sm border bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-400 text-xs font-semibold transition-colors"
                >
                  <Tag className="h-3.5 w-3.5" /> Tags <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {MERGE_TAGS.map((t) => (
                  <DropdownMenuItem key={t.token} onClick={() => editor.chain().focus().insertContent(t.token).run()} data-testid={`rte-tag-${t.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="font-medium">{t.label}</span>
                    <span className="text-xs text-neutral-400 font-mono">{t.token}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1 self-center" />
            <ToolButton label="Undo" data-testid="rte-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
              <Undo className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton label="Redo" data-testid="rte-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
              <Redo className="h-3.5 w-3.5" />
            </ToolButton>
          </>
        )}

        {mode === "source" && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 px-1">Edit raw HTML — switch to Visual to preview formatted content</span>
        )}
      </div>

      {mode === "visual" ? (
        <EditorContent editor={editor} />
      ) : (
        <Textarea
          value={sourceHtml}
          onChange={handleSourceChange}
          className="min-h-[380px] rounded-none border-0 font-mono text-xs leading-relaxed resize-y focus-visible:ring-0"
          placeholder="<p>Your HTML here...</p>"
          spellCheck={false}
          data-testid="rte-source-textarea"
        />
      )}
    </div>
  );
}
